import { DatePickerV2, Input, InputNumber, Radio, Select } from '@repo/go-ui';
import { clsx } from 'clsx';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { StatusLabel } from 'src/features/status-label';
import { usePadPreparationStore } from 'src/pages/well-pad-preparation/use-pad-preparation-store';
import { SwitchButtonGroup } from 'src/shared/components/switch-button-group';
import type { PadPreparation, RigSiteEvent } from 'src/store/well-pad-preparation/entities';

import styles from './pad-details.module.scss';

type Props = {
  pad: PadPreparation;
  isEditing: boolean;
};

export const PadDetails = observer<Props>(function PadDetails({ pad, isEditing }) {
  const { t } = useTranslation();
  const { padTypeOptions, passageTypeOptions } = usePadPreparationStore();

  const {
    padReadiness: {
      padReadiness,
      padDocumentationReadiness,
      routeReadiness,
      routeDocumentationReadiness,
      electricityReadiness,
    },
    constantValues: { unplannedWellCount },
    values: {
      landLeaseDate,
      forestProjectDate,
      padDocumentationComment,
      routeReadyDate,
      routeTypeId,
      routeComment,
      routeDocumentationDate,
      routeDocumentationComment,
      electricityDate,
      electricityComment,
    },
    rigSiteEvents,
    updateValues: updatePadValues,
  } = pad;

  const [activeRSE, setActiveRSE] = useState<RigSiteEvent>(rigSiteEvents[0]);

  const rigSiteEventSwitchButtonItems = useMemo(
    () =>
      rigSiteEvents
        .filter((rse) => rse.id !== 'blank')
        .map((rse, index) => ({ key: rse.id, title: t('wellPadPreparation:PadDetails.rseIndex', { index: index + 1 }) })),
    [rigSiteEvents, t],
  );

  const {
    values: {
      costCenter,
      engineeringSurveysComment,
      engineeringSurveysCompleted,
      equipmentLayout,
      equipmentLayoutComment,
      mobilizationStartDate,
      padReadyDate,
      padTypeId,
      readinessComment,
      requiredWorks,
      wellCount,
    },
    updateValues: updateRSEValues,
  } = activeRSE;

  const onActiveRSEChange = (key: string): void => {
    const newActiveRse = rigSiteEvents.find((rse) => rse.id === key);

    if (!newActiveRse) {
      throw new Error(`cannot find corrseponding RSE ${key}`);
    }

    setActiveRSE(newActiveRse);
  };

  return (
    <div className={styles.wrapper}>
      <SwitchButtonGroup value={activeRSE.id} items={rigSiteEventSwitchButtonItems} onChange={onActiveRSEChange} />

      <div className={styles.statusBlock}>
        <div className={styles.dataRow}>
          <div className={styles.statusBlockHeader}>
            <p className={styles.statusBlockHeader_text}>{t('labels:padReadiness')}</p>
            <StatusLabel status={padReadiness} />
          </div>
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>{t('wellPadPreparation:PadDetails.padReadyDate')}</p>
          <DatePickerV2
            placeholder={isEditing ? t('common:DatePicker.defaultPlaceholder') : undefined}
            value={padReadyDate}
            className={clsx(styles.field, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
            onChange={(v) => updateRSEValues({ padReadyDate: v })}
          />
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>{t('wellPadPreparation:PadDetails.mobilizationStartDate')}</p>
          <DatePickerV2
            value={mobilizationStartDate}
            className={clsx(styles.field, isEditing ? styles.field_disabled : styles.field_readOnly)}
            disabled
          />
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>{t('wellPadPreparation:PadDetails.plannedWellCount')}</p>
          <InputNumber
            value={wellCount}
            className={clsx(styles.field, isEditing ? styles.field_disabled : styles.field_readOnly)}
            disabled
          />
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>{t('wellPadPreparation:PadDetails.unplannedWellCount')}</p>
          <InputNumber
            value={unplannedWellCount}
            className={clsx(styles.field, isEditing ? styles.field_disabled : styles.field_readOnly)}
            disabled
          />
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>{t('wellPadPreparation:PadDetails.padType')}</p>
          <Select
            placeholder={isEditing ? t('common:chooseValue') : undefined}
            value={padTypeId}
            options={padTypeOptions}
            className={clsx(styles.field, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
            onChange={(v) => updateRSEValues({ padTypeId: v })}
          />
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>{t('wellPadPreparation:PadDetails.equipmentLayout')}</p>
          <div className={styles.valueBlock}>
            <label className={styles.radioWrapper}>
              <Radio
                checked={!equipmentLayout}
                className={clsx(!isEditing && styles.field_readOnly)}
                disabled={!isEditing}
                onChange={() => updateRSEValues({ equipmentLayout: false })}
              />
              <p>{t('common:notAvailable')}</p>
            </label>

            <label className={styles.radioWrapper}>
              <Radio
                checked={!!equipmentLayout}
                className={clsx(!isEditing && styles.field_readOnly)}
                disabled={!isEditing}
                onChange={() => updateRSEValues({ equipmentLayout: true })}
              />
              <p>{t('common:available')}</p>
            </label>

            <Input
              placeholder={isEditing ? t('common:comment') : undefined}
              value={equipmentLayoutComment}
              className={clsx(styles.field, !isEditing && styles.field_readOnly)}
              disabled={!isEditing}
              onChange={(v) => updateRSEValues({ equipmentLayoutComment: v.target.value })}
            />
          </div>
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>{t('wellPadPreparation:PadDetails.engineeringSurveys')}</p>
          <div className={styles.valueBlock}>
            <label className={styles.radioWrapper}>
              <Radio
                checked={!engineeringSurveysCompleted}
                className={clsx(!isEditing && styles.field_readOnly)}
                disabled={!isEditing}
                onChange={() => updateRSEValues({ engineeringSurveysCompleted: false })}
              />
              <p>{t('common:notAvailable')}</p>
            </label>

            <label className={styles.radioWrapper}>
              <Radio
                checked={!!engineeringSurveysCompleted}
                className={clsx(!isEditing && styles.field_readOnly)}
                disabled={!isEditing}
                onChange={() => updateRSEValues({ engineeringSurveysCompleted: true })}
              />
              <p>{t('common:available')}</p>
            </label>

            <Input
              placeholder={isEditing ? t('common:comment') : undefined}
              value={engineeringSurveysComment}
              className={clsx(styles.field, !isEditing && styles.field_readOnly)}
              disabled={!isEditing}
              onChange={(v) => updateRSEValues({ engineeringSurveysComment: v.target.value })}
            />
          </div>
        </div>

        <div className={styles.dataRow}>
          <p className={clsx(styles.dataRow_label, styles.warningText)}>{t('wellPadPreparation:PadDetails.costCenter')}</p>
          <Input
            placeholder={isEditing ? t('common:enterText') : undefined}
            value={costCenter}
            className={clsx(styles.field, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
            onChange={(v) => updateRSEValues({ costCenter: v.target.value })}
          />
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>{t('wellPadPreparation:PadDetails.requiredWorks')}</p>
          <Input
            placeholder={isEditing ? t('common:enterText') : undefined}
            value={requiredWorks}
            className={clsx(styles.field, styles.field__long, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
            onChange={(v) => updateRSEValues({ requiredWorks: v.target.value })}
          />
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>{t('wellPadPreparation:PadDetails.readinessComment')} </p>
          <Input
            value={readinessComment}
            className={clsx(styles.field, styles.field__long, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
            onChange={(v) => updateRSEValues({ readinessComment: v.target.value })}
          />
        </div>
      </div>

      <div className={styles.statusBlock}>
        <div className={styles.dataRow}>
          <div className={styles.statusBlockHeader}>
            <p className={styles.statusBlockHeader_text}>{t('labels:padDocumentationReadiness')}</p>
            <StatusLabel status={padDocumentationReadiness} />
          </div>
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>{t('wellPadPreparation:PadDetails.landLeaseDate')}</p>
          <div className={styles.valueBlock}>
            <DatePickerV2
              placeholder={isEditing ? t('common:DatePicker.defaultPlaceholder') : undefined}
              value={landLeaseDate}
              className={clsx(styles.field, !isEditing && styles.field_readOnly)}
              onChange={(v) => updatePadValues({ landLeaseDate: v })}
              disabled={!isEditing}
            />
          </div>
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>{t('wellPadPreparation:PadDetails.forestProjectDate')}</p>
          <div className={styles.valueBlock}>
            <DatePickerV2
              placeholder={isEditing ? t('common:DatePicker.defaultPlaceholder') : undefined}
              value={forestProjectDate}
              className={clsx(styles.field, !isEditing && styles.field_readOnly)}
              disabled={!isEditing}
              onChange={(v) => updatePadValues({ forestProjectDate: v })}
            />
          </div>
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>{t('wellPadPreparation:PadDetails.readinessComment')}</p>
          <Input
            value={padDocumentationComment}
            className={clsx(styles.field, styles.field__long, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
            onChange={(v) => updatePadValues({ padDocumentationComment: v.target.value })}
          />
        </div>
      </div>

      <div className={styles.statusBlock}>
        <div className={styles.dataRow}>
          <div className={styles.statusBlockHeader}>
            <p className={styles.statusBlockHeader_text}>{t('labels:routeReadiness')}</p>
            <StatusLabel status={routeReadiness} />
          </div>
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>{t('wellPadPreparation:PadDetails.readinessDate')}</p>
          <DatePickerV2
            placeholder={isEditing ? t('common:DatePicker.defaultPlaceholder') : undefined}
            value={routeReadyDate}
            className={clsx(styles.field, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
            onChange={(v) => updatePadValues({ routeReadyDate: v })}
          />
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>{t('labels:routeType')}</p>
          <Select
            placeholder={isEditing ? t('common:chooseValue') : undefined}
            value={routeTypeId}
            options={passageTypeOptions}
            className={clsx(styles.field, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
            onChange={(v) => updatePadValues({ routeTypeId: v })}
          />
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>{t('wellPadPreparation:PadDetails.readinessComment')}</p>
          <Input
            value={routeComment}
            className={clsx(styles.field, styles.field__long, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
            onChange={(v) => updatePadValues({ routeComment: v.target.value })}
          />
        </div>
      </div>

      <div className={styles.statusBlock}>
        <div className={styles.dataRow}>
          <div className={styles.statusBlockHeader}>
            <p className={styles.statusBlockHeader_text}>{t('labels:routeDocumentationReadiness')}</p>
            <StatusLabel status={routeDocumentationReadiness} />
          </div>
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>{t('wellPadPreparation:PadDetails.routeDocumentation')}</p>
          <div className={styles.valueBlock}>
            <DatePickerV2
              placeholder={isEditing ? t('common:DatePicker.defaultPlaceholder') : undefined}
              value={routeDocumentationDate}
              className={clsx(styles.field, !isEditing && styles.field_readOnly)}
              disabled={!isEditing}
              onChange={(v) => updatePadValues({ routeDocumentationDate: v })}
            />
          </div>
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>{t('wellPadPreparation:PadDetails.readinessComment')}</p>
          <Input
            value={routeDocumentationComment}
            className={clsx(styles.field, styles.field__long, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
            onChange={(v) => updatePadValues({ routeDocumentationComment: v.target.value })}
          />
        </div>
      </div>

      <div className={styles.statusBlock}>
        <div className={styles.dataRow}>
          <div className={styles.statusBlockHeader}>
            <p className={styles.statusBlockHeader_text}>{t('labels:electricityReadiness')}</p>
            <StatusLabel status={electricityReadiness} />
          </div>
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>{t('wellPadPreparation:PadDetails.readinessDate')}</p>
          <DatePickerV2
            placeholder={isEditing ? t('common:DatePicker.defaultPlaceholder') : undefined}
            value={electricityDate}
            className={clsx(styles.field, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
            onChange={(v) => updatePadValues({ electricityDate: v })}
          />
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>{t('wellPadPreparation:PadDetails.readinessComment')}</p>
          <Input
            value={electricityComment}
            className={clsx(styles.field, styles.field__long, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
            onChange={(v) => updatePadValues({ electricityComment: v.target.value })}
          />
        </div>
      </div>
    </div>
  );
});
